import { getDemographicsSummary } from '../../../services/dataQcSummaryService';
import { DemographicsSummary } from '../../../interfaces/DataQcInterface';
import WithDataWrapper from '../../reusable/WithDataWrapper';
import CustomDivider from '../../reusable/CustomDivider';
import DemographicsContentSection from './DemographicsContentSection';
import { DEMOGRAPHIC_PERCENTAGES } from '../../../constants';
import { trimTo2Decimals } from '../../../utils';
import useSchools from '../../hooks/useSchools';
import useExternalSchoolCheck from '../hooks/useExternalSchoolCheck';
import ExternalSchoolMessage from '../ExternalSchoolMessage';

import './DemographicsContent.css';

interface DemographicsContentProps {
  data: DemographicsSummary;
}

const cohortDotColors = [
  'var(--accent-green-main)',
  'var(--accent-green-dark)',
  'var(--background-yellow-green)',
  'var(--accent-green-light)',
];

const totalDotColors = ['var(--divider-50)', 'var(--divider-70)', 'var(--divider-10)', 'var(--background-meduim-gray)'];

const labelsMap: { [key: string]: string | string[] } = {
  [DEMOGRAPHIC_PERCENTAGES.RACE.PACIFIC_ISLANDER]: ['Pacific', 'Islander'],
  [DEMOGRAPHIC_PERCENTAGES.RACE.WHITE]: 'White',
  [DEMOGRAPHIC_PERCENTAGES.RACE.ASIAN]: 'Asian',
  [DEMOGRAPHIC_PERCENTAGES.RACE.UNKNOWN]: 'Unknown',
  [DEMOGRAPHIC_PERCENTAGES.RACE.AMERICAN_INDIAN]: ['American', 'Indian'],
  [DEMOGRAPHIC_PERCENTAGES.RACE.LATINX]: 'Latinx',
  [DEMOGRAPHIC_PERCENTAGES.RACE.TWO_OR_MORE]: 'Two or more',
  [DEMOGRAPHIC_PERCENTAGES.RACE.OTHER]: 'Other',
  [DEMOGRAPHIC_PERCENTAGES.RACE.BLACK]: 'Black',
};

const prepareChartData = (subjects: { [key: string]: number } | null, mainColor: string) => {
  if (!subjects) return { chartLabels: [], chartData: [], firstDatasetColor: '' };

  const firstDatasetColor = mainColor;

  const chartLabels: (string | string[])[] = [];
  const chartData: number[] = [];

  Object.entries(subjects).forEach(([key, value]) => {
    const label = labelsMap[key] || key;

    chartLabels.push(label);
    chartData.push(trimTo2Decimals(value));
  });

  return { chartLabels, chartData, firstDatasetColor };
};

const createELLDataObject = (ellData: { [key: string]: number }) => {
  let parsedELLData: { learner: number; native: number; other?: number } = {
    learner: ellData[DEMOGRAPHIC_PERCENTAGES.ENGLISH_LEARNER.LEARNER],
    native: ellData[DEMOGRAPHIC_PERCENTAGES.ENGLISH_LEARNER.NATIVE],
  };

  let otherKey = Object.keys(ellData).find(
    (key) =>
      ![DEMOGRAPHIC_PERCENTAGES.ENGLISH_LEARNER.LEARNER, DEMOGRAPHIC_PERCENTAGES.ENGLISH_LEARNER.NATIVE].includes(key)
  );
  if (otherKey) parsedELLData.other = ellData[otherKey];

  return parsedELLData;
};

function DemographicsContent({ data }: DemographicsContentProps) {
  const shouldShowExternalMessage = useExternalSchoolCheck();

  if (shouldShowExternalMessage) {
    return <ExternalSchoolMessage message="We do not collect demographic data for external schools." />;
  }

  //cohort section data
  const { student_count: cohort_student_count, demographic_percentages } = data.cohort;
  const { gender, race, student_with_disabilities, english_learner, low_income } = demographic_percentages;
  const { chartLabels, chartData, firstDatasetColor } = prepareChartData(race, '#145758');

  //total section data
  const { student_count, demographic_percentages: totalDemographicPercentages } = data.total;
  const {
    gender: totalGender,
    race: totalRace,
    student_with_disabilities: totalDisabilities,
    english_learner: totalEl,
    low_income: totalLi,
  } = totalDemographicPercentages;
  const {
    chartLabels: totalLabels,
    chartData: totalData,
    firstDatasetColor: totalColor,
  } = prepareChartData(totalRace, '#4D4D4D');

  return (
    <div className="demographics-content">
      <DemographicsContentSection
        header={{
          title: 'EOAA cohort',
          description:
            'The cohort includes all graduating seniors and non-graduating seniors who remain enrolled at the end of the year.',
        }}
        studentsCount={cohort_student_count}
        countDescription="students qualify for EOAA cohort"
        genderData={gender}
        dotColors={cohortDotColors}
        raceChartData={{ labels: chartLabels || [], data: chartData || [], color: firstDatasetColor || '' }}
        otherData={{
          lowIncome: low_income[DEMOGRAPHIC_PERCENTAGES.INCOME.LOW_INCOME],
          disabilities: student_with_disabilities[DEMOGRAPHIC_PERCENTAGES.DISABILITIES.WITH_DISABILITIES],
          englishLearner: createELLDataObject(english_learner),
        }}
      />

      <CustomDivider orientation="horizontal" className="thin" />

      <DemographicsContentSection
        header={{ title: 'All students', description: ' Students with data in any of the last four years' }}
        studentsCount={student_count}
        countDescription="students matched to demographics and transcripts"
        genderData={totalGender}
        dotColors={totalDotColors}
        raceChartData={{ labels: totalLabels, data: totalData, color: totalColor }}
        otherData={{
          lowIncome: totalLi[DEMOGRAPHIC_PERCENTAGES.INCOME.LOW_INCOME],
          disabilities: totalDisabilities[DEMOGRAPHIC_PERCENTAGES.DISABILITIES.WITH_DISABILITIES],
          englishLearner: createELLDataObject(totalEl),
        }}
        classes={['all']}
      />
    </div>
  );
}

const DemographicsContentWithData = () => {
  const { dataQcSchool } = useSchools();
  const shouldShowExternalMessage = useExternalSchoolCheck();

  if (shouldShowExternalMessage) {
    return <ExternalSchoolMessage message="We do not collect demographic data for external schools." />;
  }

  let fetchData = (eoaId: string, access_token: string) => getDemographicsSummary(eoaId, access_token, dataQcSchool);

  return (
    <WithDataWrapper
      fetchData={fetchData}
      errorMessage="Error occured while fetching demographics summary data!"
      initialState={{
        cohort: {
          demographic_percentages: {},
          student_count: 0,
        },
        total: {
          demographic_percentages: {},
          student_count: 0,
        },
      }}
      options={{
        dependencies: [dataQcSchool],
        areDependenciesRequired: false,
      }}
    >
      {(data) => <DemographicsContent data={data} />}
    </WithDataWrapper>
  );
};

export default DemographicsContentWithData;
